import { useEffect, useRef, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getSignedUrl } from "@/hooks/useProtocol";
import type { PhotoCategory, ProtocolPhoto } from "@/lib/protocol";

type PhotoGridLabels = {
  add: string;
  remove: string;
  uploadFailed: string;
  removeFailed: string;
};

type Props = {
  photos: ProtocolPhoto[];
  category: PhotoCategory;
  labels?: PhotoGridLabels;
  onAdd: (file: File, ordering: number) => Promise<void> | void;
  onRemove: (photo: ProtocolPhoto) => Promise<void> | void;
  uploading?: boolean;
  /** Maximum photos shown / addable. */
  max?: number;
};

export function PhotoGrid({
  photos,
  onAdd,
  onRemove,
  uploading,
  max = 8,
  labels,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handlePick = () => inputRef.current?.click();

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!files.length) return;
    try {
      for (let i = 0; i < files.length; i++) {
        await onAdd(files[i], photos.length + i);
      }
    } catch (err: any) {
      toast.error(
        labels?.uploadFailed ??
          err?.message ??
          "Foto konnte nicht hochgeladen werden"
      );
    }
  };

  const canAdd = photos.length < max;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4">
      {photos.map((p) => (
        <PhotoTile key={p.id} photo={p} onRemove={onRemove} labels={labels} />
      ))}
      {canAdd ? (
        <button
          type="button"
          onClick={handlePick}
          disabled={uploading}
          className="flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/60 text-emerald-600 transition hover:bg-emerald-50 disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <Plus className="h-7 w-7" />
          )}
          <span className="text-sm font-medium">
            {labels?.add ?? "Foto hinzufügen"}
          </span>
        </button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={onChange}
      />
    </div>
  );
}

function PhotoTile({
  photo,
  onRemove,
  labels,
}: {
  photo: ProtocolPhoto;
  onRemove: Props["onRemove"];
  labels?: PhotoGridLabels;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    let alive = true;
    getSignedUrl(photo.storage_path).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [photo.storage_path]);

  return (
    <div className="relative aspect-square overflow-hidden rounded-2xl bg-neutral-100">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
      <button
        type="button"
        onClick={async () => {
          setRemoving(true);
          try {
            await onRemove(photo);
          } catch (err: any) {
            toast.error(
              labels?.removeFailed ??
                err?.message ??
                "Foto konnte nicht gelöscht werden"
            );
          } finally {
            setRemoving(false);
          }
        }}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900/80 text-white backdrop-blur transition hover:bg-neutral-900"
        aria-label={labels?.remove ?? "Foto entfernen"}
      >
        {removing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <X className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}
